// ComplaintsModal — per-profile complaints (投诉). Two tabs:
//   创建投诉 (create): note (required) + optional photos
//   投诉记录 (records): list of past complaints, newest first
// Used from the actions menu on /profiles and /admin/profiles, and from the
// CRM status line.
//
// VIP 服务流程 5.0: a complaint has 等级 (普通 / 紧急), 状态 (待处理 / 处理中 /
// 已解决), 负责人 and 处理结果 (required to mark it 已解决). An urgent one
// notifies the supervisors at once and tops 今日跟进 until resolved. Records
// without those fields (older API) read as 普通 · 待处理.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, AlertTriangle, Paperclip, Loader2, User, Clock, Trash2 } from 'lucide-react';
import { profilesAPI, kpiV2API } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { QrUploadButton } from './workspace/QrUpload';

const LEVELS = [['normal', '普通', 'Normal'], ['urgent', '紧急', 'Urgent']];
const STATUSES = [['open', '待处理', 'Open'], ['in_progress', '处理中', 'In progress'], ['resolved', '已解决', 'Resolved']];
const STATUS_TONE = { open: ['#F5E6E4', '#9E2B25'], in_progress: ['#F3EADB', '#7A4A15'], resolved: ['#E7EFE9', '#3F7D5A'] };

const fmt = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleString('zh-CN', { hour12: false }); };

export default function ComplaintsModal({ profileId, profileName, isZh = true, canDelete = false, onClose, onChanged, onUpdated, profile = null }) {
  const t = (zh, en) => (isZh ? zh : en);
  const { user } = useAuth();
  const [level, setLevel] = useState('normal');
  const [editId, setEditId] = useState(null);          // complaint being handled
  const [edit, setEdit] = useState({ level: 'normal', status: 'open', assignee_user_id: '', result: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [prof, setProf] = useState(profile);
  const [tab, setTab] = useState('records');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await profilesAPI.getComplaints(profileId);
      setList(r.data?.complaints || []);
    } catch { /* */ } finally { setLoading(false); }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  // The member's coach / matchmaker are offered as 负责人, so fetch the profile
  // when the caller did not pass it in.
  useEffect(() => {
    if (prof || !profileId) return;
    profilesAPI.getById(profileId).then(r => setProf(r.data || null)).catch(() => {});
  }, [prof, profileId]);

  const assignees = (() => {
    const out = [];
    const add = (id, name) => {
      const n = Number(id);
      if (!n || out.some(x => x.id === n)) return;
      out.push({ id: n, name: name || `#${n}` });
    };
    add(user?.id, [user?.first_name, user?.last_name].filter(Boolean).join(' ') || t('我', 'Me'));
    const p = prof || {};
    add(p.assigned_coach_id, [p.coach_first_name, p.coach_last_name].filter(Boolean).join(' '));
    add(p.matchmaker_id, [p.matchmaker_first_name, p.matchmaker_last_name].filter(Boolean).join(' '));
    list.forEach(c => { add(c.assignee_user_id, c.assignee_name); add(c.created_by, c.created_by_name); });
    return out;
  })();

  const startHandle = (c) => {
    setEditId(c.id);
    setEdit({
      level: c.level || 'normal', status: c.status || 'open',
      assignee_user_id: c.assignee_user_id ? String(c.assignee_user_id) : '', result: c.result || '',
    });
  };
  const saveHandle = async (c) => {
    if (edit.status === 'resolved' && !edit.result.trim()) {
      toast.error(t('改为「已解决」时请填写处理结果', 'A result is required to resolve'));
      return;
    }
    setEditSaving(true);
    try {
      await profilesAPI.updateComplaint(profileId, c.id, {
        level: edit.level, status: edit.status,
        assignee_user_id: edit.assignee_user_id ? Number(edit.assignee_user_id) : null,
        result: edit.result.trim() || null,
      });
      toast.success(t('已保存', 'Saved'));
      setEditId(null);
      await load();
      // Not onChanged — callers treat that as a create (+1 badge).
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('保存失败', 'Save failed'));
    } finally { setEditSaving(false); }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const up = [];
      for (const f of files) {
        const r = await kpiV2API.uploadFile(f);
        const url = r.data?.url || r.data?.file_url;
        if (url) up.push({ url, name: f.name, type: f.type });
      }
      setPhotos(prev => [...prev, ...up]);
    } catch { toast.error(t('图片上传失败', 'Upload failed')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = async (cid) => {
    if (!window.confirm(t('确定删除这条投诉记录？', 'Delete this complaint record?'))) return;
    setDeletingId(cid);
    try {
      await profilesAPI.deleteComplaint(profileId, cid);
      setList(prev => prev.filter(c => c.id !== cid));
      toast.success(t('已删除', 'Deleted'));
      // Note: not calling onChanged() here — callers treat it as a create
      // (+1 badge); the count corrects on next list reload.
    } catch (e) {
      toast.error(e?.response?.data?.error || t('删除失败', 'Delete failed'));
    } finally { setDeletingId(null); }
  };

  const submit = async () => {
    if (!note.trim()) { toast.error(t('请填写备注', 'Note is required')); return; }
    setSaving(true);
    try {
      await profilesAPI.createComplaint(profileId, { note: note.trim(), photos, level });
      toast.success(level === 'urgent' ? t('紧急投诉已记录，主管已收到通知', 'Urgent complaint recorded — supervisors notified') : t('投诉已记录', 'Complaint recorded'));
      setNote(''); setPhotos([]); setLevel('normal');
      await load();
      setTab('records');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('提交失败', 'Submit failed'));
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={S.overlay}>
      <div onClick={e => e.stopPropagation()} style={S.modal}>
        <div style={S.head}>
          <h3 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 21, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8, color: '#111111' }}>
            <AlertTriangle size={18} style={{ color: '#9E2B25' }} />
            {t('投诉', 'Complaints')}{profileName ? ` · ${profileName}` : ''}
          </h3>
          <button onClick={onClose} style={S.x}><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '0 18px', borderBottom: '1px solid #E4DFD7' }}>
          {[['records', t('投诉记录', 'Records') + (list.length ? ` (${list.length})` : '')], ['create', t('创建投诉', 'New complaint')]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 13, fontWeight: tab === k ? 700 : 500, color: tab === k ? '#111111' : '#6E685F',
                borderBottom: tab === k ? '2px solid #111111' : '2px solid transparent', marginBottom: -1 }}>
              {lbl}
            </button>
          ))}
        </div>

        <div style={{ padding: 18, overflowY: 'auto' }}>
          {tab === 'create' ? (
            <>
              <label style={S.label}>{t('等级', 'Level')}</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {LEVELS.map(([v, zh, en]) => (
                  <button key={v} type="button" onClick={() => setLevel(v)}
                    style={{ padding: '6px 14px', borderRadius: 2, fontSize: 13, cursor: 'pointer', fontWeight: level === v ? 700 : 500,
                      border: `1px solid ${level === v ? (v === 'urgent' ? '#9E2B25' : '#111111') : '#E4DFD7'}`,
                      background: level === v ? (v === 'urgent' ? '#9E2B25' : '#EFEAE2') : '#FFFDF9',
                      color: level === v ? (v === 'urgent' ? '#FFFDF9' : '#111111') : '#3A3835' }}>
                    {isZh ? zh : en}
                  </button>
                ))}
              </div>
              {level === 'urgent' && (
                <div style={{ fontSize: 12, color: '#9E2B25', marginTop: -6, marginBottom: 12 }}>
                  {t('紧急投诉：主管立即收到通知，未解决前排在今日跟进第一位。', 'Urgent: supervisors are notified at once and it tops today’s follow-ups until resolved.')}
                </div>
              )}
              <label style={S.label}>{t('备注', 'Note')} <span style={{ color: '#9E2B25' }}>*</span></label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
                placeholder={t('描述投诉内容…', 'Describe the complaint…')} style={S.textarea} />

              <div style={{ marginTop: 12 }}>
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} style={{ display: 'none' }} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} style={S.attachBtn}>
                  {uploading ? <Loader2 size={14} style={{ animation: 'sc-spin 1s linear infinite' }} /> : <Paperclip size={14} />}
                  {uploading ? t('上传中…', 'Uploading…') : t('添加照片（可选）', 'Add photos (optional)')}
                </button>
                <QrUploadButton profileId={profileId} isZh={isZh} target={{ kind: 'complaint' }} style={{ marginLeft: 8, verticalAlign: 'middle' }}
                  where={`${profileName || (prof && prof.first_name) || `#${profileId}`} · ${t('投诉', 'Complaint')} · ${t('照片', 'photos')}`}
                  onFiles={(urls) => setPhotos(prev => [...prev, ...urls.map(url => ({ url, name: url.split('/').pop(), type: '' }))])} />
                {photos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {photos.map((p, i) => (
                      <div key={i} style={S.thumb}>
                        <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} style={S.thumbX}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
                <button onClick={onClose} style={S.cancelBtn}>{t('取消', 'Cancel')}</button>
                <button onClick={submit} disabled={saving || !note.trim()} style={{ ...S.submitBtn, opacity: (saving || !note.trim()) ? 0.6 : 1 }}>
                  {saving ? t('提交中…', 'Submitting…') : t('提交投诉', 'Submit')}
                </button>
              </div>
            </>
          ) : (
            <>
              {loading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#6E685F', fontSize: 13 }}>{t('加载中…', 'Loading…')}</div>
              ) : list.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#6E685F', fontSize: 13 }}>{t('暂无投诉记录', 'No complaints yet')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {list.map(c => (
                    <div key={c.id} style={S.record}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: '#6E685F', marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#3A3835', fontWeight: 600 }}><User size={11} /> {c.created_by_name || t('员工', 'Staff')}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> {fmt(c.created_at)}</span>
                          {canDelete && (
                            <button type="button" onClick={() => remove(c.id)} disabled={deletingId === c.id}
                              title={t('删除', 'Delete')}
                              style={{ display: 'inline-flex', alignItems: 'center', border: 'none', background: 'none', cursor: deletingId === c.id ? 'wait' : 'pointer', color: '#9E2B25', padding: 2 }}>
                              {deletingId === c.id ? <Loader2 size={13} style={{ animation: 'sc-spin 1s linear infinite' }} /> : <Trash2 size={13} />}
                            </button>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
                        {c.level === 'urgent' && <span style={{ ...S.tag, background: '#9E2B25', color: '#FFFDF9' }}>{t('紧急', 'Urgent')}</span>}
                        {(() => {
                          const st = c.status || 'open';
                          const [bg, fg] = STATUS_TONE[st] || STATUS_TONE.open;
                          const lbl = (STATUSES.find(x => x[0] === st) || STATUSES[0]);
                          return <span style={{ ...S.tag, background: bg, color: fg }}>{isZh ? lbl[1] : lbl[2]}</span>;
                        })()}
                        {c.assignee_name && <span style={{ fontSize: 11.5, color: '#6E685F' }}>{t('负责人', 'Owner')} {c.assignee_name}</span>}
                        {editId !== c.id && (
                          <button type="button" onClick={() => startHandle(c)}
                            style={{ marginLeft: 'auto', border: '1px solid #E4DFD7', background: '#FFFDF9', borderRadius: 2, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#111111', fontWeight: 600 }}>
                            {t('处理', 'Handle')}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#111111', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.note}</div>
                      {c.result && editId !== c.id && (
                        <div style={{ fontSize: 12.5, color: '#3F7D5A', marginTop: 6 }}>
                          {t('处理结果：', 'Result: ')}{c.result}{c.resolved_at ? ` · ${fmt(c.resolved_at)}` : ''}
                        </div>
                      )}
                      {editId === c.id && (
                        <div style={{ borderTop: '1px solid #EFEAE2', marginTop: 10, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                            <label style={S.miniLabel}>{t('等级', 'Level')}
                              <select value={edit.level} onChange={e => setEdit(x => ({ ...x, level: e.target.value }))}
                                style={{ ...S.select, ...(edit.level === 'urgent' ? { borderColor: '#9E2B25', color: '#9E2B25' } : {}) }}>
                                {LEVELS.map(([v, zh, en]) => <option key={v} value={v}>{isZh ? zh : en}</option>)}
                              </select>
                            </label>
                            <label style={S.miniLabel}>{t('状态', 'Status')}
                              <select value={edit.status} onChange={e => setEdit(x => ({ ...x, status: e.target.value }))} style={S.select}>
                                {STATUSES.map(([v, zh, en]) => <option key={v} value={v}>{isZh ? zh : en}</option>)}
                              </select>
                            </label>
                            <label style={S.miniLabel}>{t('负责人', 'Owner')}
                              <select value={edit.assignee_user_id} onChange={e => setEdit(x => ({ ...x, assignee_user_id: e.target.value }))} style={S.select}>
                                <option value="">{t('未指定', 'Unassigned')}</option>
                                {assignees.map(a => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
                              </select>
                            </label>
                          </div>
                          <label style={S.miniLabel}>{t('处理结果', 'Result')}
                            <input value={edit.result} maxLength={1000} onChange={e => setEdit(x => ({ ...x, result: e.target.value }))}
                              placeholder={t('改为「已解决」时必填', 'Required to resolve')} style={S.select} />
                          </label>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" onClick={() => setEditId(null)} style={{ ...S.cancelBtn, padding: '6px 14px', fontSize: 13 }}>{t('取消', 'Cancel')}</button>
                            <button type="button" disabled={editSaving} onClick={() => saveHandle(c)}
                              style={{ ...S.submitBtn, background: '#111111', padding: '6px 14px', fontSize: 13, opacity: editSaving ? 0.6 : 1 }}>
                              {editSaving ? t('保存中…', 'Saving…') : t('保存', 'Save')}
                            </button>
                          </div>
                        </div>
                      )}
                      {Array.isArray(c.photos) && c.photos.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                          {c.photos.map((p, i) => (
                            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={S.recThumb}>
                              <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <style>{`@keyframes sc-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 },
  modal: { background: '#FFFDF9', borderRadius: 2, width: '100%', maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'none' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', borderBottom: '1px solid #E4DFD7' },
  x: { background: 'none', border: 'none', cursor: 'pointer', color: '#6E685F' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#3A3835', marginBottom: 6 },
  textarea: { width: '100%', padding: '10px 12px', border: '1px solid #E4DFD7', borderRadius: 2, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 },
  attachBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid #E4DFD7', borderRadius: 2, background: '#FFFDF9', cursor: 'pointer', fontSize: 13, color: '#3A3835', fontWeight: 600 },
  thumb: { position: 'relative', width: 64, height: 64, borderRadius: 2, overflow: 'hidden', border: '1px solid #E4DFD7' },
  thumbX: { position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#FFFDF9', cursor: 'pointer', fontSize: 11, lineHeight: '18px', padding: 0 },
  cancelBtn: { padding: '9px 18px', border: '1px solid #E4DFD7', borderRadius: 2, background: '#FFFDF9', cursor: 'pointer', fontSize: 14 },
  submitBtn: { padding: '9px 18px', border: 'none', borderRadius: 2, background: '#9E2B25', color: '#FFFDF9', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  record: { background: '#F6F3EE', border: '1px solid #E4DFD7', borderRadius: 2, padding: '10px 12px' },
  tag: { fontSize: 11.5, fontWeight: 600, padding: '1px 8px', borderRadius: 2 },
  miniLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6E685F' },
  select: { font: 'inherit', fontSize: 13, border: '1px solid #E4DFD7', borderRadius: 2, padding: '6px 8px', background: '#FFFDF9', minWidth: 0, width: '100%', boxSizing: 'border-box' },
  recThumb: { display: 'block', width: 56, height: 56, borderRadius: 2, overflow: 'hidden', border: '1px solid #E4DFD7' },
};
